import React, { useRef, useEffect } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { AtlassianIcon } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { ButtonColor } from '@/components/adg4/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';

import { useMobileNavDisplay } from './hooks/use-mobile-nav-display';
import { useMore } from './hooks/use-more';
import { NAV_SCROLL_SHADOW, useNavScrollShadow } from './hooks/use-nav-scroll-shadow';
import { Divider } from './mobile-nav/icons/divider';
import { MobileNavLinksContainerInfo } from './mobile-nav/mobile-nav-links-container';
import { MobileNavTop } from './mobile-nav/mobile-nav-top';
import {
	ProductNavMobileMenu,
	AdditionalMenuPageObjects,
} from './mobile-nav/product-nav-mobile-menu';
import { navLayout, navGrid } from './nav-layout';
import GlobalNavLinksContainer, {
	GlobalNavLinkContentProps,
	GlobalNavProvider,
} from './nav-links-container';
import { NavUtilities } from './nav-utilities';
import SearchBar from './search-bar';
import { SkipToContent } from './skip-to-content';

const productNavHeaderCss = css({
	position: 'sticky',
	insetBlockStart: 0,
	zIndex: 5,
	transition: 'box-shadow 160ms ease-out',
	'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});
const productNavShellCss = css({
	backgroundColor: wacToken('color.neutral.light0'),
	paddingInline: navLayout.outerInset,
	boxSizing: 'border-box',
	[media.above.sm]: { padding: 0 },
});
const productNavExpandedCss = css({
	[media.below.sm]: {
		position: 'fixed',
		insetBlockStart: 0,
		insetInlineStart: 0,
		zIndex: 5,
		height: '100vh',
		width: '100%',
		overflow: 'auto',
		paddingBlockEnd: token('space.300'),
	},
});
const productNavDesktopCss = css({ display: 'none', [media.above.md]: { display: 'contents' } });
const productNavMobileControlsCss = css({
	display: 'flex',
	justifyContent: 'flex-end',
	minWidth: 0,
	[media.above.md]: { '&&': { display: 'none' } },
});
const productNavMobileMenuCss = css({
	display: 'block',
	marginBlockStart: token('space.150'),
	[media.above.sm]: { '&&': { display: 'none' } },
});
const productNavBackCss = css({
	display: 'flex',
	alignItems: 'center',
	border: 0,
	backgroundColor: 'transparent',
	padding: 0,
	color: token('color.text.selected'),
	fontSize: '0.875rem',
	fontFamily: 'inherit',
	fontWeight: 'inherit',
	lineHeight: 'inherit',
	cursor: 'pointer',
	':focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },
	[media.above.sm]: { '&&': { display: 'none' } },
});
const productNavBackDarkCss = css({ color: wacToken('color.extended.blue300') });
const productNavHideLogoCss = css({ [media.below.sm]: { '&&': { display: 'none' } } });

const productPageNavDarkThemeStyle = css({
	backgroundColor: wacToken('color.primary.black'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"button[name='sign-in'] span": {
		color: wacToken('color.neutral.light0'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.avatar-button span': {
		color: wacToken('color.neutral.light0'),
	},
});

const atlasIconContainerStyle = css({
	[media.below.sm]: { '&&': { display: 'none' } },
	height: navLayout.height,
	width: navLayout.atlassianSquareSize,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: wacToken('color.primary.blue'),
});

const atlasIconStyle = css({
	height: '56px',
	width: '56px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const productPageNavStyle = css({
	'--nav-menu-padding-block-start': token('space.075'),
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	height: navLayout.height,
	position: 'relative',
	alignItems: 'center',
	width: '100%',
	[media.above.sm]: {
		gridTemplateColumns: navGrid.productColumnsDesktop,
	},
	[media.above.lg]: {
		gridTemplateColumns: navGrid.productColumnsWide,
	},
});
const productPageNavigationCss = css({
	minWidth: 0,
	height: '100%',
	[media.above.sm]: { gridColumn: 3, paddingInlineEnd: navLayout.outerInset },
});

const productPageNavBar = css({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	minWidth: 0,
});

const productPageNavLogoAndLinksContainer = css({
	minWidth: 0,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flex: 1,
});

const productPageNavProductLogo = css({
	// This is admittedly a magic number that accounts for the tendency for our logo + wordmark lockup having descenders below the baseline
	position: 'relative',
	height: '100%',
	flexShrink: 0,
	paddingBlockStart: '3px',
	boxSizing: 'border-box',
	[media.above.md]: { paddingInlineEnd: navLayout.logoMenuGap },
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const aNavBarLogoLink = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: 'var(--product-nav-logo-height, 24px)',
});

const productNavProductArtworkCss = css({
	display: 'flex',
	height: 'var(--product-nav-logo-height, 24px)',
	// Product artwork may be wrapped (for example, Confluence).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: { display: 'flex', height: 'var(--product-nav-logo-height, 24px)', width: 'auto' },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: { height: 'var(--product-nav-logo-height, 24px)', width: 'auto', maxWidth: 'none' },
});
// GALILEO-1972

export type BasedProductPageNavProps = {
	readonly productLogo: JSX.Element;
	/** Product wordmark height in pixels, shared across all viewports. */
	readonly productLogoHeight?: number;
	readonly productLogoUrl: string;
	readonly productLogoAriaLabel: string;
	readonly productKey?: ProductKeyTypes;
	readonly productPageNavLinks: GlobalNavLinkContentProps[];
	readonly isShowSearchBarAndProfileContainer?: boolean;
	readonly isShowTryNowLink?: boolean;
	readonly tryNowCTAButtonBackgroundColor?: ButtonColor;
	readonly isShowSearchButton?: boolean;
	readonly navigationTrynowCtaText?: string;
	readonly navigationTrynowCtaUrl?: string;
	readonly navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
	readonly navigationTrynowCtaButtonVariant?: 'primary' | 'secondary';
	readonly isShowAppSwitcher?: boolean;
	readonly isShowUserProfile?: boolean;
	readonly isShowTabletDropdown?: boolean;
	readonly isTemplatesTabInHeader?: boolean;
};

export type ProductPageNavProps = BasedProductPageNavProps & {
	/** @deprecated The productLogo artwork is shared across all viewport sizes. */
	readonly mobileLogo?: JSX.Element;
	readonly mobileLinksSections: MobileNavLinksContainerInfo[];
	readonly isIncludeMobileCTA?: boolean;
	readonly mobileCtaText?: string;
	readonly mobileCtaUrl?: string;
	readonly mobileCtaOnClick?: (e: React.MouseEvent) => void;
	readonly mobileCtaButtonVariant?: 'primary' | 'secondary';
	readonly isIncludeMobileAtlassianLogo?: boolean;
	readonly additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
};

export const ProductPageNav = ({
	productLogo,
	productLogoHeight = 24,
	productLogoUrl,
	productLogoAriaLabel,
	productKey,
	productPageNavLinks,
	isShowSearchBarAndProfileContainer = true,
	isShowTryNowLink = true,
	tryNowCTAButtonBackgroundColor,
	isShowSearchButton = true,
	navigationTrynowCtaText,
	navigationTrynowCtaUrl = '/try',
	navigationTrynowCtaOnClick,
	navigationTrynowCtaButtonVariant = 'primary',
	mobileLinksSections,
	isIncludeMobileCTA = true,
	mobileCtaText,
	mobileCtaUrl = '/software/jira/free',
	mobileCtaOnClick,
	mobileCtaButtonVariant = 'primary',
	isIncludeMobileAtlassianLogo = true,
	additionalMenuPageObjectsArray = [],
	isShowAppSwitcher = true,
	isShowUserProfile = true,
	isShowTabletDropdown = true,
}: // eslint-disable-next-line complexity
ProductPageNavProps) => {
	const intl = useIntl();
	const atlasUrl = useLocalizedHref('/');
	const hasScrolled = useNavScrollShadow();
	const [isOpenSearch, setIsOpenSearch] = React.useState(false);

	const defaultCtaText = intl.formatMessage({
		id: 'productpagesubnav.try-now-button-label',
		defaultMessage: 'Get it free',
	});

	const { isDarkTheme } = useTheme();
	const themedProductLogo =
		React.isValidElement<{ darkTheme?: boolean }>(productLogo) &&
		typeof productLogo.type !== 'string'
			? React.cloneElement(productLogo, { darkTheme: isDarkTheme })
			: productLogo;

	const navRef = useRef<HTMLElement>(null);
	const mobileState = useMobileNavDisplay(isOpenSearch);
	const showMobileBack =
		mobileState.showMenu &&
		![
			'default',
			'search',
			'resources',
			'product-everyone',
			'product-developer',
			'product-product-managers',
			'product-it-professionals',
			'product-business-teams',
			'product-leadership-teams',
			'solutions',
			'why-atlassian',
		].includes(mobileState.menuPage);
	const linksRef = useRef<HTMLUListElement | null>(null);
	const searchAndProfileRef = useRef<HTMLDivElement | null>(null);
	const hasMounted = useRef(false);

	const { showMoreItems, shouldShowMoreItems, maxItems } = useMore({
		leftElementRef: linksRef,
		rightElementRef: searchAndProfileRef,
		initialMaxItems: productPageNavLinks.length,
		buffer: 24,
	});

	// Focus the search button when the search bar is closed
	useEffect(() => {
		if (hasMounted.current) {
			const searchButton = Array.from(
				navRef.current?.querySelectorAll<HTMLButtonElement>(
					'#global-nav-search-bar-icon-desktop',
				) ?? [],
			).find((button) => button.getClientRects().length > 0);
			if (searchButton) {
				searchButton.focus();
			}
		} else {
			hasMounted.current = true;
		}
	}, [isOpenSearch]);

	const mobileMenu = (
		<ProductNavMobileMenu
			state={mobileState}
			productKey={productKey as ProductKeyTypes}
			mobileLinksSections={mobileLinksSections}
			isIncludeCTA={isIncludeMobileCTA}
			ctaText={mobileCtaText ?? defaultCtaText}
			ctaUrl={mobileCtaUrl}
			ctaOnClick={mobileCtaOnClick}
			ctaButtonVariant={mobileCtaButtonVariant}
			isIncludeAtlassianLogo={isIncludeMobileAtlassianLogo}
			additionalMenuPageObjectsArray={additionalMenuPageObjectsArray}
			isShowSearchButton={isShowSearchButton}
		/>
	);
	return (
		<GlobalNavProvider globalNavItemKeys={productPageNavLinks.map((link) => link.globalLinksKey)}>
			<header
				style={{ '--product-nav-logo-height': `${productLogoHeight}px` } as React.CSSProperties}
				data-nav-dropdown-boundary
				css={[productNavHeaderCss, css({ boxShadow: hasScrolled ? NAV_SCROLL_SHADOW : 'none' })]}
				className="product-page-nav-container"
			>
				<nav
					ref={navRef}
					aria-label={intl.formatMessage({
						id: 'productPageNav.main-nav-label',
						defaultMessage: 'Main',
					})}
					css={[
						productNavShellCss,
						isDarkTheme && productPageNavDarkThemeStyle,
						mobileState.showMenu && productNavExpandedCss,
					]}
				>
					<div css={productPageNavStyle}>
						<div css={atlasIconContainerStyle}>
							<a
								css={atlasIconStyle}
								href={atlasUrl}
								aria-label={intl.formatMessage({
									id: 'productPageNav.logo-label',
									defaultMessage: 'Atlassian logo',
								})}
								data-event="clicked"
								data-event-component="linkButton"
								data-event-container="productPageNav"
								data-product-key="home"
							>
								<AtlassianIcon size="medium" appearance="inverse" />
							</a>
						</div>
						<div css={productPageNavigationCss}>
							<div className="product-page-nav-bar" css={productPageNavBar}>
								<div
									className="product-page-nav-logo-and-links-container"
									css={productPageNavLogoAndLinksContainer}
									style={{ height: '100%', alignItems: isOpenSearch ? 'center' : 'flex-end' }}
								>
									<div
										className="product-page-nav-product-logo"
										css={productPageNavProductLogo}
										style={{
											alignSelf: 'center',
										}}
									>
										<SkipToContent alignToLogo />
										<a
											className="navigation-bar-logo-link"
											css={[aNavBarLogoLink, showMobileBack && productNavHideLogoCss]}
											href={productLogoUrl}
											aria-label={productLogoAriaLabel}
											data-event="clicked"
											data-event-component="linkButton"
											data-event-container="productPageNav"
											data-label={productLogoAriaLabel}
											data-product-key={productKey ?? 'jira'}
										>
											<span css={productNavProductArtworkCss}>{themedProductLogo}</span>
										</a>
									</div>
									{showMobileBack && (
										<button
											type="button"
											css={[productNavBackCss, isDarkTheme && productNavBackDarkCss]}
											onClick={() => {
												mobileState.handleMenuPage('default');
												mobileState.handleFirstOpen(false);
											}}
										>
											<ChevronLeftIcon label="" spacing="spacious" />
											{intl.formatMessage({ id: 'globalnavv2.menu-back', defaultMessage: 'Back' })}
										</button>
									)}

									{isOpenSearch ? (
										<SearchBar
											isOpenSearch={isOpenSearch}
											setIsOpenSearch={setIsOpenSearch}
											parentType="ProductNav"
										/>
									) : (
										<div css={productNavDesktopCss}>
											<GlobalNavLinksContainer
												globalLinksProps={productPageNavLinks}
												globalLinksRef={linksRef}
												showMore={shouldShowMoreItems}
												setShowMore={showMoreItems}
												maxLinks={maxItems}
											/>
										</div>
									)}
								</div>
								<div css={productNavDesktopCss}>
									{!isOpenSearch && isShowSearchBarAndProfileContainer && !shouldShowMoreItems && (
										<NavUtilities
											isShowTryNowLink={isShowTryNowLink}
											// @ts-expect-error -- legacy CTA color prop is not declared on NavUtilitiesProps
											ctaButtonColor={tryNowCTAButtonBackgroundColor}
											productKey={productKey}
											isShowSearchButton={isShowSearchButton}
											setIsOpenSearch={setIsOpenSearch}
											navigationTrynowCtaText={navigationTrynowCtaText ?? defaultCtaText}
											navigationTrynowCtaUrl={navigationTrynowCtaUrl}
											navigationTrynowCtaOnClick={navigationTrynowCtaOnClick}
											navigationTrynowCtaButtonVariant={navigationTrynowCtaButtonVariant}
											searchAndProfileRef={searchAndProfileRef}
											isShowAppSwitcher={isShowAppSwitcher}
											isShowUserProfile={isShowUserProfile}
											navType={'productNav'}
										/>
									)}
								</div>
								{!isOpenSearch && (
									<div css={productNavMobileControlsCss}>
										<MobileNavTop
											showLogo={false}
											productKey={productKey as ProductKeyTypes}
											isLoggedIn={mobileState.userInfo.isUserLoggedIn}
											userName={mobileState.userInfo.name ?? ''}
											picture={mobileState.userInfo.picture ?? ''}
											handleClickAvatar={mobileState.handleClickAvatar}
											showMenu={mobileState.showMenu}
											handleToggleMenu={mobileState.handleToggleMenu}
											menuPage={mobileState.menuPage}
											handleMenuPage={mobileState.handleMenuPage}
											handleFirstOpen={mobileState.handleFirstOpen}
											isOpenSearch={isOpenSearch}
											setIsOpenSearch={setIsOpenSearch}
											tabletDropdownMenu={mobileMenu}
											isShowTryNowLink={isShowTryNowLink}
											navigationTrynowCtaText={mobileCtaText ?? defaultCtaText}
											navigationTrynowCtaUrl={mobileCtaUrl}
											navigationTrynowCtaOnClick={mobileCtaOnClick}
											navigationTrynowCtaButtonVariant={mobileCtaButtonVariant}
											isShowSearchButton={isShowSearchButton}
											isShowAppSwitcher={isShowAppSwitcher}
											isShowUserProfile={isShowUserProfile}
											isShowTabletDropdown={isShowTabletDropdown}
											navType="productNav"
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					{mobileState.showMenu && (
						<div css={productNavMobileMenuCss} id="nav-mobile-dropdown-menu">
							<Divider />
							{mobileMenu}
						</div>
					)}
				</nav>
			</header>
		</GlobalNavProvider>
	);
};
